'use client';
import { FC, useEffect } from 'react';
import cx from 'classnames';
import PartUnitPrice from './part-unit-price';
import { useProductDetailPageContext } from '../../context/product-detail';
import DiscountPrice from 'components/price/discount-price';
import { Conditional, useBreakpoint } from 'components/pcc-components';
import QuantityPicker from '../quantity-picker/quantity-picker';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { PartPriceProps } from './types';
import { PAGE_BREAKPOINTS } from 'constants/common';
import styles from './part-price.module.scss';
import {
  checkIsRiyal,
  getRiyalFormattedPrice
} from 'app/[lang]/[esite]/utils/paymentUtils';
import {
  getSessionStorage,
  removeSessionStorage
} from '@cat-ecom/common/utils/storageUtils';
import { PART_NUM_QUANTITY } from 'components/availability-wrapper/dealer-other-branches/constants';
import { PartPriceDesktop } from './part-price-desktop/part-price-desktop';

function isValidSessionQuantity(sessionQty: number): number | undefined {
  return sessionQty > 1 ? sessionQty : undefined;
}
const isCheckAndCondition = (value1, value2) => value1 && value2;

const isCheckEmptyString = <T, J>(condition: T, value: J) =>
  condition ? value : '';

const PartPrice: FC<PartPriceProps> = ({
  isKit,
  showNewMobileStyles,
  isUpdatePDPHeaderMobile = false,
  isRTL = false,
  currencyLdFeatureFlag = false,
  showDiscountSection = true,
  priceData,
  isHeadlessPriceAndAvailability
}) => {
  const { updateQuantity, quantityUpdatedAt } = useProductDetailPageContext();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const { items: priceItems = [] } = priceData ?? {};
  const price = priceItems[0];
  const isEmptyPrice = isEmpty(price);
  const currencyCode = priceData?.currency;
  const unitPrice = isCheckEmptyString(!isEmptyPrice, price.unitPrice);
  const totalPrice = isCheckEmptyString(!isEmptyPrice, price.totalPrice);
  const shouldDisplayPrice = isCheckAndCondition(!isEmptyPrice, !!totalPrice);
  const showUnitPrice = isCheckAndCondition(
    unitPrice,
    unitPrice !== totalPrice
  );
  const showNewMobileQuantityInput = isCheckAndCondition(
    isMD === false,
    isUpdatePDPHeaderMobile
  );
  const saudiCurrencySymbol = isCheckAndCondition(
    currencyLdFeatureFlag,
    checkIsRiyal(currencyCode)
  );
  const packageQuantity = isHeadlessPriceAndAvailability
    ? Number(price?.packageError?.packageQuantity)
    : price?.packageQty;
  // get the quantity from session storage then the p&a call to set initially

  // If there's a quantity in session storage, use it to update the quantity,
  // as package quantity is 1 by default we are checking greater than 1
  function getSessionQuantity(): number | undefined {
    const partNumber_QTY = getSessionStorage(PART_NUM_QUANTITY);
    if (!partNumber_QTY) return;
    const [, qty] = partNumber_QTY.split('_');
    const sessionQty = Number(qty);
    return isValidSessionQuantity(sessionQty);
  }

  function handleInitialQuantityUpdate() {
    if (quantityUpdatedAt) return;

    const sessionQty = getSessionQuantity();
    if (sessionQty) {
      updateQuantity(sessionQty);
      removeSessionStorage(PART_NUM_QUANTITY);
      return;
    }
    if (isCheckAndCondition(packageQuantity, packageQuantity > 1)) {
      updateQuantity(packageQuantity);
    }
  }

  useEffect(() => {
    handleInitialQuantityUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageQuantity, quantityUpdatedAt, updateQuantity]);
  return (
    shouldDisplayPrice && (
      <div
        className={cx({
          'd-flex justify-content-between': showNewMobileQuantityInput,
          [styles['is-kit-mobile']]: isCheckAndCondition(
            isKit,
            showNewMobileQuantityInput
          )
        })}
      >
        <div
          className={cx(styles['price'], {
            'py-2': !isKit
          })}
        >
          <div
            className={cx('d-lg-none d-flex', {
              'flex-column': !isKit
            })}
          >
            <div>
              {saudiCurrencySymbol ? (
                getRiyalFormattedPrice({
                  price: totalPrice,
                  priceWrapperClasses: cx(
                    'px-1',
                    {
                      [styles['price--part']]: !showNewMobileStyles,
                      [styles['price--part-mobile']]: showNewMobileStyles
                    },
                    'mb-0 '
                  ),
                  isRTL: isRTL
                })
              ) : (
                <p
                  className={cx(
                    {
                      [styles['price--part']]: !showNewMobileStyles,
                      [styles['price--part-mobile']]: showNewMobileStyles
                    },
                    'mb-0'
                  )}
                  data-testid="total-price-mobile"
                >
                  {`${totalPrice} `}
                  {showNewMobileStyles ? (
                    <span className="cat-u-theme-typography-title-sm">
                      {currencyCode}
                    </span>
                  ) : (
                    currencyCode
                  )}
                </p>
              )}
              <Conditional test={showUnitPrice} fallback={null}>
                <PartUnitPrice
                  unitPrice={unitPrice}
                  currencyCode={currencyCode}
                  showNewMobileStyles={showNewMobileStyles}
                  isRTL={isRTL}
                  currencyLdFeatureFlag={currencyLdFeatureFlag}
                />
              </Conditional>
            </div>
            <Conditional test={showDiscountSection} fallback={null}>
              <DiscountPrice
                price={price}
                isProductOverview={false}
                currency={currencyCode}
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
              />
            </Conditional>
          </div>
          <PartPriceDesktop
            showDiscountSection={showDiscountSection}
            showUnitPrice={showUnitPrice}
            saudiCurrencySymbol={saudiCurrencySymbol}
            totalPrice={totalPrice}
            currencyCode={currencyCode}
            unitPrice={unitPrice}
            price={price}
            isRTL={isRTL}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
          />
        </div>
        <Conditional test={showNewMobileQuantityInput}>
          <QuantityPicker isUpdatePDPHeaderMobile={isUpdatePDPHeaderMobile} />
        </Conditional>
      </div>
    )
  );
};

export default PartPrice;
